import { createContext, useContext, useMemo } from 'react';

import type { CreativeChatSettings } from '@/components/create/v2/types';

interface CreateSettingsContextValue {
  settings: CreativeChatSettings;
  updateSetting: <K extends keyof CreativeChatSettings>(
    key: K,
    value: CreativeChatSettings[K],
  ) => void;
}

const CreateSettingsContext = createContext<CreateSettingsContextValue | null>(null);

export function CreateSettingsProvider({
  settings,
  updateSetting,
  children,
}: CreateSettingsContextValue & { children: React.ReactNode }) {
  const value = useMemo(() => ({ settings, updateSetting }), [settings, updateSetting]);
  return <CreateSettingsContext value={value}>{children}</CreateSettingsContext>;
}

export function useCreateSettings(): CreateSettingsContextValue {
  const ctx = useContext(CreateSettingsContext);
  if (!ctx) throw new Error('useCreateSettings must be used within CreateSettingsProvider');
  return ctx;
}

export function useOptionalCreateSettings(): CreateSettingsContextValue | null {
  return useContext(CreateSettingsContext);
}
